import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Activity, Pill, FlaskConical, Wind, Stethoscope, Baby, Droplet,
  AlertTriangle, ClipboardList, TrendingUp, Sun, HeartPulse,
  ArrowUpRight, Copy, Pencil, Check, X,
} from "lucide-react";

const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-", "Unknown"];
const EDITABLE_FIELDS = [
  "diagnosis", "consultant", "gestational_age_weeks", "birth_weight_g",
  "current_weight_g", "blood_group", "mother_name", "father_name", "phone", "address",
];

const VITAL_RANGES = {
  temperature_c: [36.0, 37.8],
  heart_rate: [100, 170],
  resp_rate: [30, 65],
  spo2: [90, 100],
  blood_sugar: [45, 180],
};
function vitalTone(k, v) {
  const r = VITAL_RANGES[k]; if (!r || v == null) return "";
  if (v > r[1]) return "status-critical";
  if (v < r[0]) return "status-warn";
  return "status-vacant";
}

const MODE_LABEL = {
  VENT_SIMV: "Ventilator (SIMV)", VENT_AC: "Ventilator (AC)", VENT_PSV: "Ventilator (PSV)",
  VENT_HFOV: "Ventilator (HFOV)", VENT_NIV: "NIV", CPAP: "CPAP", HFNC: "HFNC",
  LF_O2: "Low-flow O₂", ROOM_AIR: "Room air",
};

const fmtDT = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false });
  } catch { return String(iso).replace("T", " ").slice(0, 16); }
};

export default function SummaryTab({ patient, onJump, onPatientChange }) {
  const [data, setData] = useState({});
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({});

  const startEdit = () => {
    const seed = {};
    EDITABLE_FIELDS.forEach((k) => { seed[k] = patient[k] ?? ""; });
    setDraft(seed);
    setEditing(true);
  };
  const cancelEdit = () => { setEditing(false); setDraft({}); };
  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = {};
      EDITABLE_FIELDS.forEach((k) => {
        const v = draft[k];
        if (v === "" || v == null) { payload[k] = null; return; }
        if (["gestational_age_weeks", "birth_weight_g", "current_weight_g"].includes(k)) {
          const n = Number(v);
          payload[k] = Number.isFinite(n) ? n : null;
        } else {
          payload[k] = v;
        }
      });
      const { data: updated } = await api.patch(`/patients/${patient.id}`, payload);
      onPatientChange?.(updated);
      setEditing(false);
      toast.success("Patient details updated");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to update";
      toast.error(typeof msg === "string" ? msg : "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        const [vitals, orders, admins, invs, resp, notes, lines, procs] = await Promise.all([
          api.get(`/vitals/${patient.id}`).then((r) => r.data),
          api.get(`/medications/orders/${patient.id}`).then((r) => r.data),
          api.get(`/medications/administrations/${patient.id}`).then((r) => r.data),
          api.get(`/investigations/${patient.id}`).then((r) => r.data),
          api.get(`/respiratory/${patient.id}/active`).then((r) => r.data),
          api.get(`/notes/${patient.id}`).then((r) => r.data).catch(() => []),
          api.get(`/lines/${patient.id}`).then((r) => r.data).catch(() => []),
          api.get(`/procedures/${patient.id}`).then((r) => r.data).catch(() => []),
        ]);
        if (!ok) return;
        setData({ vitals, orders, admins, invs, resp, notes, lines, procs });
      } catch (e) { console.warn("Summary load failed", e); }
    })();
    return () => { ok = false; };
  }, [patient.id]);

  const latestVital = data.vitals?.[0];
  const activeMeds = (data.orders || []).filter((o) => o.active);
  const stoppedMeds = (data.orders || []).filter((o) => !o.active).slice(0, 3);
  const latestByCat = {};
  (data.invs || []).forEach((i) => { if (!latestByCat[i.category]) latestByCat[i.category] = i; });
  const activeLines = (data.lines || []).filter((l) => l.active);
  const latestNote = data.notes?.[0];
  const weightTrend = (data.vitals || [])
    .filter((v) => v.weight_g)
    .slice(0, 30)
    .reverse()
    .map((v) => ({ t: (v.at || "").slice(5, 10), w: v.weight_g }));

  const lastAdminFor = (orderId) => (data.admins || []).find((a) => a.order_id === orderId);

  const onCopy = async () => {
    try {
      const md = buildMarkdown(patient, data);
      await navigator.clipboard.writeText(md);
      toast.success("Summary copied as markdown");
    } catch (e) {
      console.warn("Copy failed", e);
      toast.error("Copy failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="summary-tab">
      {/* Toolbar */}
      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          className="rounded-full"
          onClick={onCopy}
          data-testid="btn-copy-summary"
        >
          <Copy size={13} className="mr-1.5" /> Copy summary
        </Button>
      </div>

      {/* Top row — Overview + Weight trend */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card-clinical p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Baby size={14} className="text-primary" />
              <span className="text-xs uppercase tracking-widest text-muted-foreground">Overview</span>
            </div>
            {!editing ? (
              <Button
                variant="outline"
                size="sm"
                className="rounded-full h-7 px-3 text-[11px]"
                onClick={startEdit}
                data-testid="btn-edit-overview"
              >
                <Pencil size={12} className="mr-1.5" /> Edit
              </Button>
            ) : (
              <div className="flex items-center gap-1.5">
                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-full h-7 px-3 text-[11px]"
                  onClick={cancelEdit}
                  disabled={saving}
                  data-testid="btn-cancel-overview"
                >
                  <X size={12} className="mr-1" /> Cancel
                </Button>
                <Button
                  size="sm"
                  className="rounded-full h-7 px-3 text-[11px]"
                  onClick={saveEdit}
                  disabled={saving}
                  data-testid="btn-save-overview"
                >
                  <Check size={12} className="mr-1" /> {saving ? "Saving…" : "Save"}
                </Button>
              </div>
            )}
          </div>
          {editing ? (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <EditCell label="Diagnosis" span={4}>
                <Textarea
                  rows={2}
                  value={draft.diagnosis ?? ""}
                  onChange={(e) => setField("diagnosis", e.target.value)}
                  placeholder="e.g., Preterm 34w, RDS, Neonatal sepsis"
                  data-testid="edit-diagnosis"
                />
              </EditCell>
              <EditCell label="Consultant" span={2}>
                <Input value={draft.consultant ?? ""} onChange={(e) => setField("consultant", e.target.value)} data-testid="edit-consultant" />
              </EditCell>
              <EditCell label="Blood group" span={2}>
                <Select value={draft.blood_group || ""} onValueChange={(v) => setField("blood_group", v)}>
                  <SelectTrigger data-testid="edit-blood-group"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {BLOOD_GROUPS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                  </SelectContent>
                </Select>
              </EditCell>
              <EditCell label="Gestational age (wk)">
                <Input type="number" step="0.1" value={draft.gestational_age_weeks ?? ""} onChange={(e) => setField("gestational_age_weeks", e.target.value)} data-testid="edit-ga" />
              </EditCell>
              <EditCell label="Birth weight (g)">
                <Input type="number" step="1" value={draft.birth_weight_g ?? ""} onChange={(e) => setField("birth_weight_g", e.target.value)} data-testid="edit-bw" />
              </EditCell>
              <EditCell label="Current weight (g)">
                <Input type="number" step="1" value={draft.current_weight_g ?? ""} onChange={(e) => setField("current_weight_g", e.target.value)} data-testid="edit-cw" />
              </EditCell>
              <EditCell label="Phone">
                <Input value={draft.phone ?? ""} onChange={(e) => setField("phone", e.target.value)} data-testid="edit-phone" />
              </EditCell>
              <EditCell label="Mother's name" span={2}>
                <Input value={draft.mother_name ?? ""} onChange={(e) => setField("mother_name", e.target.value)} data-testid="edit-mother" />
              </EditCell>
              <EditCell label="Father's name" span={2}>
                <Input value={draft.father_name ?? ""} onChange={(e) => setField("father_name", e.target.value)} data-testid="edit-father" />
              </EditCell>
              <EditCell label="Address" span={4}>
                <Textarea rows={2} value={draft.address ?? ""} onChange={(e) => setField("address", e.target.value)} data-testid="edit-address" />
              </EditCell>
              <div className="md:col-span-4 text-[11px] text-muted-foreground">
                Bed, admission date and DOL can't be edited here — use the Transfer flow for bed changes.
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Cell label="Diagnosis" value={patient.diagnosis || "—"} span={2} />
              <Cell label="Consultant" value={patient.consultant || "—"} />
              <Cell label="Bed" value={patient.bed_number || "—"} />
              <Cell label="Gestational age" value={patient.gestational_age_weeks ? `${patient.gestational_age_weeks} wk` : "—"} />
              <Cell label="DOL" value={patient.day_of_life ?? "—"} />
              <Cell label="Birth weight" value={patient.birth_weight_g ? `${patient.birth_weight_g} g` : "—"} />
              <Cell label="Current weight" value={patient.current_weight_g ? `${patient.current_weight_g} g` : "—"} />
              <Cell label="Blood group" value={patient.blood_group || "—"} />
              <Cell label="Mother" value={patient.mother_name || "—"} />
              <Cell label="Father" value={patient.father_name || "—"} />
              <Cell label="Admitted" value={fmtDT(patient.admission_date)} />
            </div>
          )}
          {!editing && (
            <div className="mt-4 flex flex-wrap gap-2">
              {patient.critical && <Badge variant="destructive" className="rounded-full text-[10px]"><AlertTriangle size={11} className="mr-1" /> CRITICAL</Badge>}
              {patient.phototherapy && <Badge className="rounded-full text-[10px] bg-purple-500 hover:bg-purple-500"><Sun size={11} className="mr-1" /> Phototherapy</Badge>}
              {patient.status === "discharged" && <Badge className="rounded-full text-[10px]">Discharged</Badge>}
              {patient.demo && <Badge variant="outline" className="rounded-full text-[10px]">Demo</Badge>}
            </div>
          )}
        </div>

        <div className="card-clinical p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <TrendingUp size={14} className="text-primary" />
              <span className="text-xs uppercase tracking-widest text-muted-foreground">Weight trend</span>
            </div>
            <JumpBtn onJump={onJump} target="growth" label="Growth" />
          </div>
          <div className="h-32">
            {weightTrend.length > 1 ? (
              <ResponsiveContainer>
                <LineChart data={weightTrend}>
                  <XAxis dataKey="t" stroke="#94a3b8" fontSize={10} />
                  <YAxis stroke="#94a3b8" fontSize={10} domain={["auto", "auto"]} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                  <Line type="monotone" dataKey="w" stroke="#10b981" strokeWidth={2} dot={{ r: 2 }} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full grid place-items-center text-xs text-muted-foreground">Weight will appear after ≥2 recorded values</div>
            )}
          </div>
        </div>
      </div>

      {/* Latest vitals + Active respiratory */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card-clinical p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <HeartPulse size={14} className="text-primary" />
              <span className="text-xs uppercase tracking-widest text-muted-foreground">Latest vitals</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground tabular">{latestVital ? fmtDT(latestVital.at) : "—"}</span>
              <JumpBtn onJump={onJump} target="vitals" label="Vitals" />
            </div>
          </div>
          {latestVital ? (
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
              {[
                ["temperature_c", "Temp °C"],
                ["heart_rate", "HR"],
                ["resp_rate", "RR"],
                ["spo2", "SpO₂"],
                ["blood_sugar", "Sugar"],
                ["weight_g", "Wt g"],
              ].map(([k, l]) => (
                <div key={k} className="rounded-xl bg-muted/40 px-3 py-2">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{l}</div>
                  <div className={`font-display font-semibold tabular text-xl ${vitalTone(k, latestVital[k])}`}>
                    {latestVital[k] ?? "—"}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">No vitals recorded yet.</div>
          )}
        </div>

        <div className="card-clinical p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Wind size={14} className="text-primary" />
              <span className="text-xs uppercase tracking-widest text-muted-foreground">Respiratory support</span>
            </div>
            <JumpBtn onJump={onJump} target="vent" label="Respiratory" />
          </div>
          {data.resp ? (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className="rounded-full text-[11px]">{MODE_LABEL[data.resp.mode] || data.resp.mode}</Badge>
                <span className="text-[11px] text-muted-foreground">since {fmtDT(data.resp.started_at)}</span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {Object.entries(data.resp.current_settings || {}).map(([k, v]) => (
                  <div key={k} className="rounded-md bg-muted/40 px-2 py-1.5">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{k}</div>
                    <div className="font-mono tabular text-sm">{v}</div>
                  </div>
                ))}
                {Object.keys(data.resp.current_settings || {}).length === 0 && (
                  <div className="col-span-2 text-xs text-muted-foreground">No settings needed for this mode.</div>
                )}
              </div>
            </>
          ) : (
            <div className="text-sm text-muted-foreground flex items-center gap-2"><Wind size={14} /> Room air</div>
          )}
        </div>
      </div>

      {/* Active medications */}
      <div className="card-clinical p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Pill size={14} className="text-primary" />
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Current medications ({activeMeds.length})</span>
          </div>
          <div className="flex items-center gap-2">
            {stoppedMeds.length > 0 && (
              <span className="text-[11px] text-muted-foreground">{stoppedMeds.length} recently stopped</span>
            )}
            <JumpBtn onJump={onJump} target="meds" label="Medications" />
          </div>
        </div>
        {activeMeds.length === 0 ? (
          <div className="text-sm text-muted-foreground">No active drug orders.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {activeMeds.map((o) => {
              const lg = lastAdminFor(o.id);
              return (
                <div key={o.id} className="rounded-lg border border-border/60 px-3 py-2 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{o.name} <span className="text-xs text-muted-foreground">{o.dose} · {o.route} · {o.frequency}</span></div>
                    <div className="text-[11px] text-muted-foreground tabular mt-0.5">
                      {lg ? `last given ${fmtDT(lg.given_at)} by ${lg.given_by}` : "not yet administered"}
                    </div>
                    {(o.interaction_warnings || []).slice(0, 1).map((w, i) => (
                      <div key={`${o.id}-int-${i}`} className={`text-[10px] mt-0.5 ${w.severity === "major" ? "status-critical" : "status-warn"}`} title={w.text}>
                        ↔ {w.with} ({w.severity})
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent labs + Active lines */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card-clinical p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <FlaskConical size={14} className="text-primary" />
              <span className="text-xs uppercase tracking-widest text-muted-foreground">Latest lab / investigation per category</span>
            </div>
            <JumpBtn onJump={onJump} target="inv" label="Investigations" />
          </div>
          {Object.keys(latestByCat).length === 0 ? (
            <div className="text-sm text-muted-foreground">No investigations recorded.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {Object.values(latestByCat).map((i) => (
                <div key={i.id} className="rounded-lg border border-border/60 px-3 py-2">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium">{i.category}</div>
                    <div className="text-[11px] tabular text-muted-foreground">{fmtDT(i.at)}</div>
                  </div>
                  <div className="text-xs font-mono text-muted-foreground mt-1 line-clamp-2">
                    {Object.entries(i.values || {}).slice(0, 6).map(([k, v]) => `${k}=${v}`).join(" · ") || "(no values)"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card-clinical p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Droplet size={14} className="text-primary" />
              <span className="text-xs uppercase tracking-widest text-muted-foreground">Active lines ({activeLines.length})</span>
            </div>
            <JumpBtn onJump={onJump} target="growth" label="Lines" />
          </div>
          {activeLines.length === 0 ? (
            <div className="text-sm text-muted-foreground">No indwelling lines.</div>
          ) : (
            <div className="space-y-2">
              {activeLines.map((l) => {
                const bg = l.risk === "critical" ? "bg-critical" : l.risk === "warn" ? "bg-warn" : "bg-vacant";
                return (
                  <div key={l.id} className={`rounded-lg px-3 py-2 flex items-center justify-between ${bg}`}>
                    <div className="text-sm">
                      <span className="font-medium">{l.kind}</span> · {l.name}
                    </div>
                    <div className="tabular text-xs">{Math.floor(l.days)}/{l.max_days} d</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Latest doctor round */}
      <div className="card-clinical p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Stethoscope size={14} className="text-primary" />
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Latest doctor round</span>
          </div>
          <JumpBtn onJump={onJump} target="notes" label="Doctor notes" />
        </div>
        {latestNote ? (
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground tabular">{latestNote.date} · {latestNote.updated_by || latestNote.created_by || "—"}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <NoteBlock label="Examination" text={latestNote.examination} />
              <NoteBlock label="Systemic" text={latestNote.systemic} />
              <NoteBlock label="Assessment" text={latestNote.assessment} />
              <NoteBlock label="Plan" text={latestNote.plan} />
            </div>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">No doctor notes yet.</div>
        )}
      </div>
    </div>
  );
}

function Cell({ label, value, span }) {
  return (
    <div className={`space-y-1 ${span === 2 ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-sm font-medium truncate" title={String(value)}>{value ?? "—"}</div>
    </div>
  );
}

function EditCell({ label, span, children }) {
  const spanCls = span === 4 ? "md:col-span-4" : span === 2 ? "md:col-span-2" : "";
  return (
    <div className={`space-y-1 ${spanCls}`}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      {children}
    </div>
  );
}

function NoteBlock({ label, text }) {
  return (
    <div className="rounded-lg bg-muted/30 px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="mt-1 whitespace-pre-wrap text-sm">{text?.trim() || "—"}</div>
    </div>
  );
}

function JumpBtn({ onJump, target, label }) {
  if (!onJump) return null;
  return (
    <button
      type="button"
      onClick={() => onJump(target)}
      title={`Open ${label}`}
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
      data-testid={`jump-${target}`}
    >
      Open <ArrowUpRight size={11} />
    </button>
  );
}

function buildMarkdown(patient, data) {
  const p = patient || {};
  const lv = data.vitals?.[0];
  const activeMeds = (data.orders || []).filter((o) => o.active);
  const latestByCat = {};
  (data.invs || []).forEach((i) => { if (!latestByCat[i.category]) latestByCat[i.category] = i; });
  const activeLines = (data.lines || []).filter((l) => l.active);
  const note = data.notes?.[0];
  const resp = data.resp;

  const lines = [];
  lines.push(`# NICU Summary — ${p.name || "—"}`);
  lines.push(`_${p.hospital_id || ""} · Bed ${p.bed_number || "—"} · ${p.nicu_area || "—"}_`);
  lines.push("");
  lines.push("## Overview");
  lines.push(`- Diagnosis: ${p.diagnosis || "—"}`);
  lines.push(`- Consultant: ${p.consultant || "—"}`);
  lines.push(`- GA: ${p.gestational_age_weeks ? `${p.gestational_age_weeks} wk` : "—"} · DOL: ${p.day_of_life ?? "—"}`);
  lines.push(`- Birth weight: ${p.birth_weight_g ? `${p.birth_weight_g} g` : "—"} · Current weight: ${p.current_weight_g ? `${p.current_weight_g} g` : "—"}`);
  lines.push(`- Admitted: ${fmtDT(p.admission_date)}`);
  const flags = [
    p.critical && "CRITICAL",
    p.phototherapy && "Phototherapy",
    p.status === "discharged" && "Discharged",
  ].filter(Boolean);
  if (flags.length) lines.push(`- Flags: ${flags.join(", ")}`);

  lines.push("");
  lines.push(`## Latest vitals${lv ? ` (${fmtDT(lv.at)})` : ""}`);
  if (lv) {
    lines.push(`- Temp: ${lv.temperature_c ?? "—"} °C · HR: ${lv.heart_rate ?? "—"} · RR: ${lv.resp_rate ?? "—"} · SpO₂: ${lv.spo2 ?? "—"} · Sugar: ${lv.blood_sugar ?? "—"} · Wt: ${lv.weight_g ?? "—"} g`);
  } else {
    lines.push("- No vitals recorded.");
  }

  lines.push("");
  lines.push("## Respiratory support");
  if (resp) {
    lines.push(`- Mode: ${MODE_LABEL[resp.mode] || resp.mode} (since ${fmtDT(resp.started_at)})`);
    const settings = Object.entries(resp.current_settings || {}).map(([k, v]) => `${k}=${v}`).join(", ");
    if (settings) lines.push(`- Settings: ${settings}`);
  } else {
    lines.push("- Room air");
  }

  lines.push("");
  lines.push(`## Current medications (${activeMeds.length})`);
  if (activeMeds.length === 0) {
    lines.push("- None");
  } else {
    activeMeds.forEach((o) => {
      lines.push(`- ${o.name} — ${o.dose} · ${o.route} · ${o.frequency}`);
    });
  }

  lines.push("");
  lines.push("## Latest investigations");
  if (Object.keys(latestByCat).length === 0) {
    lines.push("- None recorded");
  } else {
    Object.values(latestByCat).forEach((i) => {
      const vals = Object.entries(i.values || {}).slice(0, 6).map(([k, v]) => `${k}=${v}`).join(", ");
      lines.push(`- ${i.category} (${fmtDT(i.at)}): ${vals || "(no values)"}`);
    });
  }

  lines.push("");
  lines.push(`## Active lines (${activeLines.length})`);
  if (activeLines.length === 0) {
    lines.push("- None");
  } else {
    activeLines.forEach((l) => {
      lines.push(`- ${l.kind} — ${l.name} — ${Math.floor(l.days)}/${l.max_days} d`);
    });
  }

  lines.push("");
  lines.push("## Latest doctor round");
  if (note) {
    lines.push(`_${note.date} · ${note.updated_by || note.created_by || "—"}_`);
    if (note.examination) lines.push(`- Examination: ${note.examination.trim()}`);
    if (note.systemic) lines.push(`- Systemic: ${note.systemic.trim()}`);
    if (note.assessment) lines.push(`- Assessment: ${note.assessment.trim()}`);
    if (note.plan) lines.push(`- Plan: ${note.plan.trim()}`);
  } else {
    lines.push("- No doctor notes yet.");
  }

  return lines.join("\n");
}
